import {TABS} from './reference'

/**
 * The panel's view state as the server's list request — pure, and deliberately import-light.
 *
 * Kept out of ./api.js so it can be unit-tested on its own: importing that module pulls in the
 * app-wide axios layer, which Jest cannot transform, and the mapping is the part most worth pinning.
 * Every value here has a matching field on the backend's WdV2ListRequest.
 */

/**
 * The panel's view state as the server's list request.
 *
 * Exported and pure so the mapping is testable on its own: every value here has a matching field on
 * WdV2ListRequest, and the urgency bands (crit/warn/ok/expiring48) are the same four names on both
 * sides. `page` is 0-based on the wire and 1-based in the panel, which is the one place the two
 * disagree and the one place it is converted.
 */
export function toListBody({tab, filters, range, sort, page, size} = {}) {
  const f = filters || {}
  const body = {
    tab: tab === 'analytics' || tab === 'sku' || !tab ? 'all' : tab,
    sort: sort || 'urgency',
    page: Math.max(0, (page || 1) - 1),
    size: Math.min(Math.max(1, size || DEFAULT_PAGE_SIZE), 200),
  }
  if (f.q && f.q.trim()) body.q = f.q.trim()
  if (f.courier) body.courier = f.courier
  if (f.urgency) body.urgency = f.urgency
  const awbs = splitAwbs(f.awbs)
  if (awbs.length) body.awbs = awbs
  // A status narrows WITHIN the tab: statusIn overrides the tab server-side, so one from another tab
  // (left over from before a tab change) is dropped rather than allowed to show that tab's rows here.
  const tabDef = TABS.find((t) => t.id === body.tab)
  if (f.status && (!tabDef || !tabDef.statuses || tabDef.statuses.includes(f.status))) body.statusIn = [toApiStatus(f.status)]
  if (f.sku && f.sku.trim()) body.sku = f.sku.trim()
  if (Number(f.minExcessKg) > 0) body.minExcessKg = Number(f.minExcessKg)
  if (Number(f.minExcessAmount) > 0) body.minExcessAmount = Number(f.minExcessAmount)
  const window = rangeToDates(range)
  if (window.fromDate) body.fromDate = window.fromDate
  if (window.toDate) body.toDate = window.toDate
  return body
}

/**
 * Excess weight as the merchant is billed it (28 Oct, business): courier-billed slab − brand-pushed slab, both on
 * the merchant's own rate card. Rows raised before slabs were recorded have no slab figure and keep the raw
 * difference. Never negative-zero noise: missing both gives 0, as the mapping always has.
 */
export function excessKgOf(r) {
  if (!r) return 0
  if (r.slabDiffKg != null) return r.slabDiffKg
  return r.excessKg != null ? r.excessKg : 0
}

/** AWBs pasted one per line, or separated by commas or spaces — at most 200. */
export function splitAwbs(text) {
  return Array.from(new Set(String(text || '').split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean))).slice(0, 200)
}

// The panel says AUTO_ACCEPTED; the server's enum is AUTO_ACCEPTED_BY_SYSTEM (see api.js's STATUS_FROM_API).
const toApiStatus = (s) => (s === 'AUTO_ACCEPTED' ? 'AUTO_ACCEPTED_BY_SYSTEM' : s)

/** yyyy-MM-dd, the format WdV2ListRequest parses (IST day boundaries server-side). */
function ymd(d) {
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * The date filter as a server window. '7d'/'30d'/'90d' become a from-date; an explicit
 * {startDate, endDate} passes through; 'all' sends no window at all, which is the one case that
 * makes the server read a merchant's entire history.
 */
export function rangeToDates(range) {
  if (!range || range === 'all') return {}
  if (typeof range === 'string') {
    const days = {'7d': 7, '30d': 30, '90d': 90}[range]
    if (!days) return {}
    return {fromDate: ymd(new Date(Date.now() - days * 86400000))}
  }
  const out = {}
  if (range.startDate) out.fromDate = ymd(new Date(range.startDate))
  if (range.endDate) out.toDate = ymd(new Date(range.endDate))
  return out
}

/** The panel's own default page size when it does not name one. */
export const DEFAULT_PAGE_SIZE = 10

/**
 * A server SkuRow as the SKU table renders it.
 *
 * The backend field names were chosen to match state/analytics.js#skuBreakdown so the tab reads a
 * server row exactly where it used to read a computed one. The one genuine difference is the weight
 * buckets: the wire carries {weightKg, count} (WdV2Summary.WeightBucket) and the table wants
 * {weight, count}.
 */
export function mapSkuRow(r) {
  if (!r) return null
  return {
    sku: r.sku,
    product: r.product,
    openCount: r.openCount || 0,
    exposure: r.exposure || 0,
    modalWeight: r.modalWeight,
    declaredVolKg: r.declaredVolKg,
    declaredDims: r.declaredDims || null,
    suggestedWeight: r.suggestedWeight,
    deadKg: r.deadKg,
    volKg: r.volKg,
    dims: r.dims || null,
    offModal: r.offModal || 0,
    shipments: r.shipments == null ? r.openCount || 0 : r.shipments,
    weights: (r.weights || []).map((w) => ({weight: w.weightKg, count: w.count})),
  }
}
